import BlockOutlined from "@mui/icons-material/BlockOutlined";
import HelpOutlineOutlined from "@mui/icons-material/HelpOutlineOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";
import { Box, Typography } from "@mui/material";
import { type FC, type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../../../components/UIToolkit/Badges/MetaChip";
import { SystemRoundedCheckbox } from "../../../../../components/UIToolkit/Clickable/SystemRoundedCheckbox";
import { RecordSetDiff } from "../../../../../components/UIToolkit/Tables/RecordSetDiff/RecordSetDiff";
import { ValueDiffPair } from "../../../../../components/UIToolkit/Tables/ValueDiffPair/ValueDiffPair";
import { DIFF_REVIEW, FONT_SIZE } from "../../../../../theme/tokens";
import { AIRTABLE_KIND_LABEL_KEY, AIRTABLE_KIND_TONE, recordDiffLabels, recordSetsFrom, valueLines } from "./airtableImportLabels";
import type { AirtableRowKind } from "./airtableImportTypes";
import type { AirtableReviewEntry } from "./useAirtableReview";

/**
 * The four cell renderers behind `AirtableDiffGrid`, split out so the grid file stays a column
 * definition rather than a column definition wrapped around four screens of JSX.
 */

/**
 * The accept control sits on the FIELD NAME's line, not the row's centre: a row is as tall as its
 * longest value list, so centring drifts the box further from the name it answers for the taller the
 * row gets — which is exactly the row where being sure what you are accepting matters most.
 */
const ACCEPT_CELL_SX = { display: "flex", alignItems: "flex-start", justifyContent: "center", width: "100%", pt: 1.25 } as const;

/** Only the un-acceptable kinds carry a glyph — the ordinary rows are the majority and stay quiet. */
const KIND_ICON: Partial<Record<AirtableRowKind, ReactNode>> = {
	unresolvable: <HelpOutlineOutlined fontSize="inherit" />,
	out_of_band: <ReportProblemOutlined fontSize="inherit" />,
	blocked: <BlockOutlined fontSize="inherit" />,
	// A padlock, not a bar: `blocked` is a refusal about this department's data, `protected` is a
	// standing property of the field. The two must not read as the same sentence.
	protected: <LockOutlined fontSize="inherit" />,
};

export const AcceptCell: FC<{ entry: AirtableReviewEntry; disabled: boolean; onCheckedChange: (rowId: string, checked: boolean) => void }> = ({
	entry,
	disabled,
	onCheckedChange,
}) => {
	const { t } = useTranslation();
	if (!entry.acceptable) {
		return (
			<Box sx={ACCEPT_CELL_SX} aria-hidden>
				<Box component="span" sx={{ color: "text.disabled" }}>
					—
				</Box>
			</Box>
		);
	}
	return (
		<Box sx={ACCEPT_CELL_SX}>
			<SystemRoundedCheckbox
				checked={entry.checked}
				disabled={disabled}
				onChange={(next) => onCheckedChange(entry.rowId, next)}
				ariaLabel={t("department.config.airtable.accept.ariaLabel", { field: t(entry.row.labelKey) })}
				data-testid={`airtable-accept-${entry.rowId}`}
			/>
		</Box>
	);
};

export const FieldCell: FC<{ entry: AirtableReviewEntry }> = ({ entry }) => {
	const { t } = useTranslation();
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 1, minWidth: 0 }}>
			<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: "text.primary", lineHeight: 1.3, wordBreak: "break-word" }}>
				{t(entry.row.labelKey)}
			</Typography>
			<MetaChip
				label={t(AIRTABLE_KIND_LABEL_KEY[entry.row.kind])}
				tone={AIRTABLE_KIND_TONE[entry.row.kind]}
				icon={KIND_ICON[entry.row.kind]}
				size="small"
				sx={{ alignSelf: "flex-start" }}
				dataTestId={`airtable-kind-${entry.rowId}`}
			/>
		</Box>
	);
};

export const ValuesCell: FC<{ entry: AirtableReviewEntry }> = ({ entry }) => {
	const { t } = useTranslation();

	// A LIST OF RULE OBJECTS gets the record-shaped diff: break rules and overtime rules are objects of
	// twelve and seventeen members, so the flat pair renders five dense lines and leaves the reviewer to
	// find the one number that moved. `recordSetsFrom` returns null for every other shape — a scalar, a
	// list of strings (`jobIds`), an object of arrays (`fullShiftTimes`) — and those keep the line pair,
	// which is the right rendering for them.
	const records = useMemo(() => recordSetsFrom(entry.row.currentValue, entry.row.incomingValue, t), [entry.row.currentValue, entry.row.incomingValue, t]);
	const labels = useMemo(() => recordDiffLabels(t), [t]);

	if (records && records.current.length + records.incoming.length > 0) {
		return (
			<Box sx={{ py: 1, width: "100%", minWidth: 0 }}>
				<RecordSetDiff
					currentRecords={records.current}
					incomingRecords={records.incoming}
					labels={labels}
					maxHeight={DIFF_REVIEW.RECORD_LIST_MAX_HEIGHT}
					dataTestId={`airtable-records-${entry.rowId}`}
				/>
			</Box>
		);
	}

	const currentLines = valueLines(entry.row.currentValue, t);
	const incomingLines = valueLines(entry.row.incomingValue, t);
	return (
		<Box sx={{ py: 1, width: "100%", minWidth: 0 }}>
			<ValueDiffPair
				currentLabel={t("department.config.airtable.column.current")}
				incomingLabel={t("department.config.airtable.column.incoming")}
				currentLines={currentLines}
				incomingLines={incomingLines}
				emptyLabel={t("department.config.airtable.value.empty")}
				// `replace` only for LIST-shaped values, which is where the destruction it marks actually
				// happens: four break rules becoming three. On a scalar (100 → 125) the strikethrough-and-red
				// treatment is theatre — it fires on every row and stops meaning anything, which is precisely
				// when the reader stops seeing the rows that really are losing data.
				mode={currentLines.length > 1 || incomingLines.length > 1 ? "replace" : "plain"}
				maxHeight={DIFF_REVIEW.ROW_MAX_HEIGHT}
				dataTestId={`airtable-diff-${entry.rowId}`}
			/>
		</Box>
	);
};

export const NotesCell: FC<{ entry: AirtableReviewEntry }> = ({ entry }) => {
	const { t } = useTranslation();

	// The server's own reason wins: on a `blocked` or `out_of_band` row it says something specific,
	// where the unknown-field note is ours and generic. It only speaks when there is nothing better.
	const reasonKey = entry.row.reasonKey ?? (entry.unknownField ? "department.config.airtable.reason.unknownField" : null);
	const band = entry.row.band;
	if (!reasonKey && !band) return null;

	// The note has to agree with the row's own chip: a `protected` row is amber because nothing is
	// wrong — the field is simply outside this flow's authority — and a red sentence beside an amber
	// chip reads as two different verdicts on one row.
	const reasonColor = entry.acceptable ? "text.secondary" : entry.row.kind === "protected" ? "warning.dark" : "error.dark";
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 1, minWidth: 0 }}>
			{reasonKey && <Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: reasonColor, lineHeight: 1.4 }}>{t(reasonKey)}</Typography>}
			{/* The range is what turns "out of range" into something actionable: paired with the offending
			    value already shown in the INCOMING panel, it names the mistake (a 3.31 HPPD figure that
			    landed in a 50–150 percent field). */}
			{band && (
				<Typography data-testid={`airtable-band-${entry.rowId}`} sx={{ fontSize: FONT_SIZE.SECONDARY, color: "error.dark", fontWeight: 600, lineHeight: 1.4 }}>
					{t("department.config.airtable.reason.expectedRange", { minimum: band.minimum, maximum: band.maximum })}
				</Typography>
			)}
		</Box>
	);
};
